// AdminAnwesenheitPage — Dozentin dokumentiert Präsenz-/Online-Termine (z. B. Basiskurs Intensivpflege, 40 Std. Präsenzpflicht)
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarCheck, Download, Loader2, MapPin, Pencil, Plus, Trash2, Video, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; } };
const fmtH = (h) => String(h).replace('.', ',');

const emptyForm = {
  date: new Date().toISOString().slice(0, 10), time_from: '09:00', time_to: '17:00',
  session_type: 'praesenz', topic: '', location: '', dozent: '', attendees: [],
};

const SessionDialog = ({ courseId, participants, initial, onClose, onSaved, getAuthHeader }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calcHours = () => {
    try {
      const [fh, fm] = form.time_from.split(':').map(Number);
      const [th, tm] = form.time_to.split(':').map(Number);
      return Math.max(((th * 60 + tm) - (fh * 60 + fm)) / 60, 0);
    } catch { return 0; }
  };

  const toggleAttendee = (userId) => {
    setForm((f) => {
      const has = f.attendees.some((a) => a.user_id === userId);
      return { ...f, attendees: has ? f.attendees.filter((a) => a.user_id !== userId) : [...f.attendees, { user_id: userId, hours: null }] };
    });
  };
  const setAttendeeHours = (userId, hours) => {
    setForm((f) => ({ ...f, attendees: f.attendees.map((a) => (a.user_id === userId ? { ...a, hours: hours === '' ? null : parseFloat(hours) } : a)) }));
  };

  const save = async () => {
    if (form.attendees.length === 0) { toast.error('Bitte mindestens eine/n Teilnehmer/in anhaken'); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.put(`${API}/admin/anwesenheit/sessions/${initial.id}`, form, getAuthHeader());
      } else {
        await axios.post(`${API}/admin/anwesenheit/${courseId}/sessions`, form, getAuthHeader());
      }
      toast.success('Termin gespeichert');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const hours = calcHours();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" data-testid="anwesenheit-dialog">
      <div className="bg-cream-50 rounded-2xl p-6 w-full max-w-xl my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-espresso">{initial?.id ? 'Termin bearbeiten' : 'Neuer Termin'}</h3>
          <button onClick={onClose} className="text-sand-600 hover:text-espresso" data-testid="anwesenheit-dialog-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-sand-600 col-span-2 sm:col-span-1">Datum
            <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)}
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-date" />
          </label>
          <label className="text-xs font-semibold text-sand-600 col-span-2 sm:col-span-1">Typ
            <select value={form.session_type} onChange={(e) => set('session_type', e.target.value)}
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-type">
              <option value="praesenz">Präsenz (zählt zur Pflicht)</option>
              <option value="online">Online (nachrichtlich)</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-sand-600">Von
            <input type="time" value={form.time_from} onChange={(e) => set('time_from', e.target.value)}
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-from" />
          </label>
          <label className="text-xs font-semibold text-sand-600">Bis
            <input type="time" value={form.time_to} onChange={(e) => set('time_to', e.target.value)}
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-to" />
          </label>
          <label className="text-xs font-semibold text-sand-600 col-span-2">Thema
            <input value={form.topic} onChange={(e) => set('topic', e.target.value)} placeholder="z. B. Trachealkanülenmanagement"
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-topic" />
          </label>
          <label className="text-xs font-semibold text-sand-600">Ort
            <input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="z. B. Erkrath"
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-location" />
          </label>
          <label className="text-xs font-semibold text-sand-600">Dozent/in
            <input value={form.dozent} onChange={(e) => set('dozent', e.target.value)} placeholder="Name"
              className="mt-1 w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-dozent" />
          </label>
        </div>
        <p className="text-xs text-sand-500 mt-2">Dauer laut Uhrzeit: <b>{fmtH(Math.round(hours * 100) / 100)} Zeitstunden</b></p>

        <div className="mt-4">
          <p className="text-xs font-semibold text-sand-600 mb-2">Anwesende Teilnehmer/innen (Stunden anpassbar, z. B. bei Teilnahme nur am Vormittag)</p>
          <div className="max-h-52 overflow-y-auto space-y-1.5 border border-cream-300 rounded-lg p-2 bg-white">
            {participants.map((p) => {
              const att = form.attendees.find((a) => a.user_id === p.user_id);
              return (
                <div key={p.user_id} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-cream-100">
                  <input type="checkbox" checked={!!att} onChange={() => toggleAttendee(p.user_id)}
                    className="accent-terra w-4 h-4" data-testid={`anwesenheit-attendee-${p.user_id}`} />
                  <span className="text-sm text-espresso flex-1">{p.user_name}</span>
                  {att && (
                    <input type="number" step="0.25" min="0" placeholder={fmtH(Math.round(hours * 100) / 100)}
                      value={att.hours ?? ''} onChange={(e) => setAttendeeHours(p.user_id, e.target.value)}
                      className="w-20 border border-cream-300 rounded px-2 py-1 text-xs" title="Stunden (leer = volle Dauer)" />
                  )}
                </div>
              );
            })}
            {participants.length === 0 && <p className="text-xs text-sand-500 p-2">Keine Teilnehmer/innen im Kurs zugewiesen.</p>}
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-cream-300 text-espresso hover:bg-cream-100">Abbrechen</button>
          <button onClick={save} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold bg-espresso text-cream-50 hover:opacity-90"
            data-testid="anwesenheit-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminAnwesenheitPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // null | {} | session

  const load = useCallback((cid) => {
    axios.get(`${API}/admin/anwesenheit/${cid}`, getAuthHeader())
      .then((r) => setData(r.data)).catch(() => toast.error('Laden fehlgeschlagen'));
  }, [getAuthHeader]);

  useEffect(() => {
    axios.get(`${API}/admin/anwesenheit-courses`, getAuthHeader()).then((r) => {
      setCourses(r.data.courses);
      if (r.data.courses[0]) { setCourseId(r.data.courses[0].course_id); load(r.data.courses[0].course_id); }
    }).catch(() => toast.error('Laden fehlgeschlagen'));
  }, [getAuthHeader, load]);

  const remove = async (id) => {
    if (!window.confirm('Termin wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/anwesenheit/sessions/${id}`, getAuthHeader()); load(courseId); toast.success('Gelöscht'); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const downloadPdf = async (p) => {
    try {
      const r = await axios.get(`${API}/admin/anwesenheit/${courseId}/pdf/${p.user_id}`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Anwesenheitsnachweis_${p.user_name.replace(/ /g, '_')}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const course = courses.find((c) => c.course_id === courseId);
  return (
    <Layout>
      <div className="max-w-4xl" data-testid="admin-anwesenheit-page">
        <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.25em] text-terra">Admin · Präsenzpflicht</p>
        <h1 className="font-display text-3xl sm:text-4xl text-espresso mt-1">Anwesenheit</h1>
        <p className="text-sm text-sand-500 mt-2 mb-6">
          Präsenz- und Online-Termine dokumentieren. {course && <>Pflicht: <b>{course.required_hours} Zeitstunden in Präsenz</b> · {course.title}</>}
        </p>

        {courses.length > 1 && (
          <select value={courseId} onChange={(e) => { setCourseId(e.target.value); load(e.target.value); }}
            className="mb-5 border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="anwesenheit-course-select">
            {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
          </select>
        )}

        {!data ? (
          <div className="flex items-center gap-2 text-sand-500 text-sm py-10"><Loader2 className="w-4 h-4 animate-spin" /> Lade …</div>
        ) : (
          <>
            {/* Teilnehmer-Stände */}
            <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="anwesenheit-participants">
              {data.participants.map((p) => {
                const done = p.praesenz_hours >= data.required_hours;
                const pct = Math.min(100, Math.round((p.praesenz_hours / data.required_hours) * 100));
                return (
                  <div key={p.user_id} className="bg-cream-50 border border-cream-300 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-espresso text-sm">{p.user_name}</span>
                      <button onClick={() => downloadPdf(p)} className="text-sand-500 hover:text-espresso" title="Anwesenheitsnachweis (PDF)"
                        data-testid={`anwesenheit-pdf-${p.user_id}`}>
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-xs mt-1.5 mb-1.5">
                      <span className={done ? 'text-green-700 font-bold' : 'text-espresso font-bold'}>
                        {fmtH(p.praesenz_hours)} / {data.required_hours} Std. Präsenz{done && ' ✓'}
                      </span>
                      {p.online_hours > 0 && <span className="text-sand-500"> · + {fmtH(p.online_hours)} Std. online</span>}
                    </div>
                    <div className="h-1.5 rounded-full bg-cream-300 overflow-hidden">
                      <div className={`h-full rounded-full ${done ? 'bg-green-600' : 'bg-terra'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {data.participants.length === 0 && <p className="text-sm text-sand-500">Diesem Kurs sind noch keine Teilnehmer/innen zugewiesen.</p>}
            </div>

            {/* Termine */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl text-espresso">Termine</h2>
              <button onClick={() => setDialog({})}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-espresso text-cream-50 hover:opacity-90"
                data-testid="anwesenheit-new-btn">
                <Plus className="w-3.5 h-3.5" /> Neuer Termin
              </button>
            </div>
            <div className="space-y-2" data-testid="anwesenheit-session-list">
              {data.sessions.map((s) => (
                <div key={s.id} className="bg-cream-50 border border-cream-300 rounded-xl px-4 py-3 flex items-center gap-3">
                  {s.session_type === 'praesenz' ? <MapPin className="w-4 h-4 text-terra shrink-0" /> : <Video className="w-4 h-4 text-sand-500 shrink-0" />}
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-semibold text-espresso">{fmtDate(s.date)}</span>
                    {s.time_from && <span className="text-sand-500"> · {s.time_from}–{s.time_to}</span>}
                    <span className="text-sand-500"> · {fmtH(s.hours)} Std. · {s.session_type === 'praesenz' ? 'Präsenz' : 'Online'}</span>
                    <div className="text-xs text-espresso/80 truncate">
                      {s.topic || '—'}{s.location && ` · ${s.location}`}{s.dozent && ` · ${s.dozent}`}
                      {' · '}<span className="text-sand-500">{s.attendees.map((a) => a.user_name).join(', ') || 'keine Teilnehmer'}</span>
                    </div>
                  </div>
                  <button onClick={() => setDialog({ ...s, attendees: s.attendees.map((a) => ({ user_id: a.user_id, hours: a.hours })) })}
                    className="text-sand-500 hover:text-espresso" data-testid={`anwesenheit-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s.id)} className="text-sand-500 hover:text-red-600" data-testid={`anwesenheit-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {data.sessions.length === 0 && (
                <div className="text-center py-10 border border-dashed border-cream-300 rounded-xl">
                  <CalendarCheck className="w-8 h-8 text-sand-400 mx-auto mb-2" />
                  <p className="text-sm text-sand-500">Noch keine Termine — lege den ersten Präsenztermin an.</p>
                </div>
              )}
            </div>
          </>
        )}

        {dialog && (
          <SessionDialog courseId={courseId} participants={data?.participants || []}
            initial={dialog.id ? dialog : null} getAuthHeader={getAuthHeader}
            onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(courseId); }} />
        )}
      </div>
    </Layout>
  );
}
